import { deleteUser } from 'firebase/auth';
import { doc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Service pour gérer la suppression de compte utilisateur
 */
export const accountService = {
  /**
   * Supprime complètement un compte utilisateur et toutes ses données associées
   * @param {Object} user - L'utilisateur Firebase Auth
   * @param {Object} userProfile - Le profil utilisateur depuis Firestore
   * @returns {Promise<void>}
   */
  async deleteAccount(user, userProfile) {
    if (!user || !userProfile) {
      throw new Error('Utilisateur ou profil manquant');
    }

    const batch = writeBatch(db);
    
    try {
      // 1. Supprimer le profil utilisateur principal
      const userDocRef = doc(db, 'users', user.uid);
      batch.delete(userDocRef);

      // 2. Supprimer les réservations associées
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('clientEmail', '==', user.email)
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      
      bookingsSnapshot.forEach((bookingDoc) => {
        batch.delete(bookingDoc.ref);
      });

      // 3. Si c'est un salon owner, supprimer aussi les réservations de son salon
      if (userProfile.role === 'salon') {
        const salonBookingsQuery = query(
          collection(db, 'bookings'),
          where('salonId', '==', user.uid)
        );
        const salonBookingsSnapshot = await getDocs(salonBookingsQuery);
        
        salonBookingsSnapshot.forEach((bookingDoc) => {
          batch.delete(bookingDoc.ref);
        });

        // Supprimer les avis du salon
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('salonId', '==', user.uid)
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        
        reviewsSnapshot.forEach((reviewDoc) => {
          batch.delete(reviewDoc.ref);
        });
      }

      // 4. Supprimer les avis laissés par l'utilisateur
      const userReviewsQuery = query(
        collection(db, 'reviews'),
        where('clientEmail', '==', user.email)
      );
      const userReviewsSnapshot = await getDocs(userReviewsQuery);
      
      userReviewsSnapshot.forEach((reviewDoc) => {
        batch.delete(reviewDoc.ref);
      });

      // 5. Exécuter toutes les suppressions Firestore
      await batch.commit();

      // 6. Supprimer le compte Firebase Auth (doit être fait en dernier)
      await deleteUser(user);

      console.log('Compte supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression du compte:', error);
      throw error;
    }
  },

  /**
   * Vérifie si l'utilisateur peut supprimer son compte
   * @param {Object} userProfile - Le profil utilisateur
   * @returns {Object} - { canDelete: boolean, reason?: string }
   */
  canDeleteAccount(userProfile) {
    // Vérifier s'il y a des réservations futures pour un salon
    if (userProfile.role === 'salon') {
      // Note: Cette vérification pourrait être améliorée en vérifiant
      // réellement les réservations futures dans la base de données
      return {
        canDelete: true,
        warning: 'La suppression de votre compte annulera toutes les réservations futures de votre salon.'
      };
    }

    return { canDelete: true };
  }
};